import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/api/queryKeys';
import { DEFAULT_SETTINGS, getSettings, type Settings } from '@/lib/api/settings';
import { setActiveCurrency } from '@/lib/money';
import { useAuthState } from '@/lib/auth/authStore';

/**
 * Public/business settings (tax %, rooms_enabled, shifts_enabled, currency, capacity, print toggles...).
 * Available to every authenticated role. Falls back to safe defaults while loading / on error,
 * so `settings.rooms_enabled` is always a boolean.
 *
 * After changing settings call `invalidateSettings()` (or put the PATCH response with `setSettings`)
 * so the sidebar and every consumer update immediately.
 */
export function useSettings() {
  const { status } = useAuthState();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.settings.all,
    queryFn: getSettings,
    enabled: status === 'authenticated',
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  if (query.data) setActiveCurrency(query.data.currency);

  return {
    settings: query.data ?? DEFAULT_SETTINGS,
    /** true once real data has arrived (use to avoid flashing defaults). */
    isReady: query.isSuccess,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    setSettings: (s: Settings) => queryClient.setQueryData(queryKeys.settings.all, s),
    invalidateSettings: () => queryClient.invalidateQueries({ queryKey: queryKeys.settings.all }),
  };
}
